"use client"

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

/**
 * Registry of every top-level modal in the app. Add a new entry here to make a
 * modal openable from anywhere via `useModal(id)`.
 */
export type ModalId = string

type ModalContextValue = {
  openModals: ReadonlySet<ModalId>
  open: (id: ModalId) => void
  close: (id: ModalId) => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [openModals, setOpenModals] = useState<ReadonlySet<ModalId>>(
    () => new Set(),
  )

  const open = useCallback((id: ModalId) => {
    setOpenModals((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  const close = useCallback((id: ModalId) => {
    setOpenModals((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ openModals, open, close }),
    [openModals, open, close],
  )

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
}

const useModalContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider")
  }
  return context
}

/**
 * Scoped controls for a single modal. Returns the same `{ isOpen, open, close }`
 * shape regardless of which modal you address, so triggers and modal components
 * stay decoupled from the underlying registry.
 */
export const useModal = (id: ModalId) => {
  const { openModals, open, close } = useModalContext()

  return useMemo(
    () => ({
      isOpen: openModals.has(id),
      open: () => open(id),
      close: () => close(id),
    }),
    [openModals, id, open, close],
  )
}
